import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, SquareCheckBig } from "lucide-react";

import { visibilityOptions } from "../../shared";
import { useSelector } from "react-redux";
import {
    createUserPrivacies,
    getUserPrivacies,
    selectedProfileSetting,
    updateUserPrivacies,
    useAppDispatch,
} from "../../../redux";
import { RoleEnum } from "../../shared/model";
import Card from "../../../components/ui/card";
import { Switch } from "../../../components/ui/switch";
export default function PrivacyComponent() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { userPrivacy } = useSelector((state: any) => state.client);
    const { user } = useSelector((state: any) => state.auth);
    const [settings, setSettings] = useState(() => {
        return {
            privacy: {
                profileVisibility: "",
                showBudget: false,
                showContact: false,
                showLocation: false,
                showEarnings: false,
                showAvailability: false,
            },
        };
    });

    useEffect(() => {
        if (userPrivacy) {
            setSettings(prev => ({
                ...prev, // preserve existing privacy settings
                privacy: {
                    profileVisibility: userPrivacy.visibility?.id || "",
                    showBudget: userPrivacy.isShowBudget || false,
                    showContact: userPrivacy.isShowContact || false,
                    showLocation: userPrivacy.isShowLocation || false,
                    showEarnings: userPrivacy.isShowEarnings || false,
                    showAvailability: userPrivacy.isShowAvailability || false,
                },
            }));
        }
    }, [userPrivacy]);
    const handleInputChange = (section, field, value) => {
        setSettings(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value,
            },
        }));
    };
    useEffect(() => {
        getUserNotificationData();
    }, []);
    const getUserNotificationData = async () => {
        await dispatch(getUserPrivacies({ id: user?.id }));
    };
    const handlePrivacySubmit = async () => {
        const payload = {
            userId: {
                id: user?.id,
            },
            visibility: {
                id: Number(settings.privacy.profileVisibility),
            },
            isShowLocation: settings.privacy.showLocation,
            isShowContact: settings.privacy.showContact,
            isShowBudget: settings.privacy.showBudget,
            isShowEarnings: settings.privacy.showEarnings,
            isShowAvailability: settings.privacy.showAvailability,
        };

        if (userPrivacy?.id) {
            await dispatch(updateUserPrivacies({ id: userPrivacy.id, data: payload }));
        } else {
            await dispatch(createUserPrivacies({ data: payload }));
        }
    };
    return (
        <>
            <Card color={undefined} className="p-6">
                <h2 className="text-xl font-semibold mb-6">{t("privacySettings.title")}</h2>

                <div className="space-y-6">
                    {/* Profile Visibility */}
                    <div>
                        <label className="block text-sm font-medium mb-2">{t("privacySettings.profileVisibility")}</label>
                        <select
                            value={settings.privacy.profileVisibility}
                            onChange={e => handleInputChange("privacy", "profileVisibility", e.target.value)}
                            className="w-full p-2 border rounded-lg">
                            <option value="" disabled>
                                {t("privacySettings.selectVisibility")}
                            </option>
                            {visibilityOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Privacy Toggles */}
                    {Number(user.role.id) === RoleEnum.client ?
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-medium">{t("privacySettings.showBudget")}</p>
                                    <p className="text-sm text-gray-600">{t("privacySettings.showBudgetDesc")}</p>
                                </div>
                                <Switch
                                    checked={settings.privacy.showBudget}
                                    onCheckedChange={checked => handleInputChange("privacy", "showBudget", checked)}
                                />
                            </div>

                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-medium">{t("privacySettings.showContact")}</p>
                                    <p className="text-sm text-gray-600">{t("privacySettings.showContactDesc")}</p>
                                </div>
                                <Switch
                                    checked={settings.privacy.showContact}
                                    onCheckedChange={checked => handleInputChange("privacy", "showContact", checked)}
                                />
                            </div>

                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-medium">{t("privacySettings.showLocation")}</p>
                                    <p className="text-sm text-gray-600">{t("privacySettings.showLocationDesc")}</p>
                                </div>
                                <Switch
                                    checked={settings.privacy.showLocation}
                                    onCheckedChange={checked => handleInputChange("privacy", "showLocation", checked)}
                                />
                            </div>
                        </div>
                        :
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-medium">{t("privacySettings.showEarnings")}</p>
                                    <p className="text-sm text-gray-600">{t("privacySettings.showEarningsDesc")}</p>
                                </div>
                                <Switch
                                    checked={settings.privacy.showEarnings}
                                    onCheckedChange={checked => handleInputChange("privacy", "showEarnings", checked)}
                                />
                            </div>

                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-medium">{t("privacySettings.showAvailability")}</p>
                                    <p className="text-sm text-gray-600">{t("privacySettings.showAvailabilityDesc")}</p>
                                </div>
                                <Switch
                                    checked={settings.privacy.showAvailability}
                                    onCheckedChange={checked => handleInputChange("privacy", "showAvailability", checked)}
                                />
                            </div>
                        </div>
                    }

                </div>
            </Card>
            <div className="flex justify-end mt-4">
                {userPrivacy == null ? (
                    <button
                        className="px-4 py-2 text-white rounded-md flex items-center
      bg-blue-600 hover:bg-blue-700"
                        onClick={handlePrivacySubmit}>
                        <SquareCheckBig className="h-5" />
                        <span className="pl-2">{t("privacySettings.saveChanges")}</span>
                    </button>
                ) : (
                    <button
                        className={"px-4 py-2 text-white rounded-md flex items-center bg-blue-600 hover:bg-blue-700"}
                        onClick={handlePrivacySubmit}>
                        <SquareCheckBig className="h-5" />
                        <span className="pl-2">{t("privacySettings.updateChanges")}</span>
                    </button>
                )}
            </div>
        </>
    );
}
